import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  User, Phone, Mail, Clock, ArrowRight, CheckCircle2,
} from "lucide-react";

const BEST_TIME_OPTIONS = [
  { value: "", label: "Any time works" },
  { value: "morning", label: "Morning (8am – 12pm)" },
  { value: "afternoon", label: "Afternoon (12pm – 4pm)" },
  { value: "evening", label: "Evening (4pm – 6pm)" },
];

function CallbackForm({ api, initialNotes }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    best_time_to_call: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const isValid =
    form.customer_name.trim() &&
    form.customer_phone.trim() &&
    form.customer_email.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      toast.error("Please fill in your name, phone, and email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${api}/bookings`, {
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        customer_email: form.customer_email.trim(),
        best_time_to_call: form.best_time_to_call,
        notes: initialNotes || "",
      });
      setConfirmed(true);
      toast.success("Callback requested!");
    } catch (err) {
      toast.error(
        err.response?.data?.detail
          ? typeof err.response.data.detail === "string"
            ? err.response.data.detail
            : "Please check your details and try again."
          : "Something went wrong. Please call us at (613) 822-2333."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setConfirmed(false);
    setForm({
      customer_name: "",
      customer_phone: "",
      customer_email: "",
      best_time_to_call: "",
    });
  };

  return (
    <div
      data-testid="booking-wizard"
      className="glass-strong rounded-2xl p-6 sm:p-8 relative grain overflow-hidden"
    >
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5C518] font-bold">
          Book Now
        </div>
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl uppercase tracking-tight mt-1">
          Request a Callback
        </h2>
        <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
          Once you submit, a member of our team will contact you directly to confirm your booking and details.
        </p>
      </div>

      <AnimatePresence mode="wait">
        {confirmed ? (
          <motion.div
            key="confirmed"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="py-8 text-center"
            data-testid="booking-confirmed"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
              className="w-16 h-16 mx-auto rounded-full bg-[#F5C518] flex items-center justify-center mb-4"
            >
              <CheckCircle2 className="w-9 h-9 text-[#121212]" strokeWidth={2.5} />
            </motion.div>
            <h3 className="font-display font-extrabold text-2xl uppercase tracking-tight mb-2">
              Request Received
            </h3>
            <p className="text-neutral-400 text-sm leading-relaxed max-w-xs mx-auto">
              Thanks — a member of our team will call you shortly at{" "}
              <span className="text-[#F5C518] font-semibold">{form.customer_phone}</span>
              {form.best_time_to_call
                ? ` during the ${form.best_time_to_call}.`
                : "."}
            </p>
            <button
              onClick={reset}
              data-testid="booking-new-button"
              className="mt-6 text-xs uppercase tracking-wider text-[#F5C518] hover:text-[#FFD642] font-semibold"
            >
              Request Another Callback →
            </button>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="space-y-3"
          >
            {initialNotes && (
              <div className="mb-1 px-3 py-2 rounded-lg border border-[#F5C518]/30 bg-[#F5C518]/5 text-xs text-[#F5C518]">
                <span className="uppercase tracking-wider font-semibold mr-2">Topic:</span>
                {initialNotes}
              </div>
            )}

            <FieldRow
              icon={User}
              testId="input-name"
              type="text"
              placeholder="Full name"
              autoComplete="name"
              value={form.customer_name}
              onChange={update("customer_name")}
              required
            />
            <FieldRow
              icon={Phone}
              testId="input-phone"
              type="tel"
              placeholder="Phone number"
              autoComplete="tel"
              value={form.customer_phone}
              onChange={update("customer_phone")}
              required
            />
            <FieldRow
              icon={Mail}
              testId="input-email"
              type="email"
              placeholder="Email address"
              autoComplete="email"
              value={form.customer_email}
              onChange={update("customer_email")}
              required
            />

            <div className="relative">
              <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500 pointer-events-none" />
              <select
                data-testid="input-best-time"
                value={form.best_time_to_call}
                onChange={update("best_time_to_call")}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-white focus:border-[#F5C518] focus:outline-none focus:ring-1 focus:ring-[#F5C518]/30 [color-scheme:dark] appearance-none"
              >
                {BEST_TIME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.value ? `Best time: ${o.label}` : `Best time to call — ${o.label}`}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="wizard-next-button"
              className="glow-yellow w-full bg-[#F5C518] hover:bg-[#FFD642] disabled:opacity-60 disabled:cursor-not-allowed text-[#121212] font-display font-bold uppercase text-xs tracking-wider px-6 py-3.5 rounded-full flex items-center justify-center gap-2 mt-2"
            >
              {submitting ? "Sending..." : "Request Call"}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>

            <p className="text-[11px] text-neutral-500 text-center pt-1">
              Prefer to talk now? Call{" "}
              <a href="tel:6138222333" className="text-[#F5C518] hover:text-[#FFD642] font-semibold">
                (613) 822-2333
              </a>
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}

function FieldRow({ icon: Icon, testId, ...inputProps }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
      <input
        data-testid={testId}
        {...inputProps}
        className="w-full pl-10 pr-4 py-3 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-white placeholder:text-neutral-500 focus:border-[#F5C518] focus:outline-none focus:ring-1 focus:ring-[#F5C518]/30 transition-colors"
      />
    </div>
  );
}

export default CallbackForm;
